import {
  createMultiStyleConfigHelpers,
  StyleThemingProps,
} from '@chakra-ui/styled-system'
import { siteNavigationColorKeys } from '~/pages/Home/SiteNavigation/componentStyles/_colors'
import { asChakraVariable } from '~/themes/colors/colors'
import { textStyleKeys } from '~/themes/textStyles'
import { useCustomMultiStyleConfig } from '~/utils/chakraUtils'
import { getName } from '~/utils/objectUtils'

export enum SiteNavigationCollapseTabSizes {
  Normal = 'normal',
  Collapsed = 'collapsed',
}

const SiteNavigationCollapseTabParts = [
  'container',
  'contentWrapper',
  'arrowIconWrapper',
  'arrowIcon',
  'settingsIconWrapper',
  'settingsIcon',
  'text',
] as const

const { Content, Icon, FontColor } =
  siteNavigationColorKeys.SiteNavigationCollapseTab

const {
  Background: SiteNavigationTabBackground,
  BoxShadow: SiteNavigationTabBoxShadow,
} = siteNavigationColorKeys.SiteNavigationTab
const helpers = createMultiStyleConfigHelpers(SiteNavigationCollapseTabParts)

export const SiteNavigationCollapseTabStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
    },
    contentWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      height: '1.75rem',
      px: '0.75rem',
      flex: 1,
      cursor: 'pointer',
      borderRadius: '2.5rem',
      _hover: {
        bg: Content.Background.Hover,
      },
      _active: {
        bg: Content.Background.Active,
      },
    },
    arrowIcon: {
      color: Icon.Default,
    },
    settingsIconWrapper: {
      height: '2rem',
      width: '2rem',
      display: 'flex',
      direction: 'column',
      justifyContent: 'center',
      borderRadius: '0.5rem',
      alignItems: 'center',
      flexShrink: '0',
      cursor: 'pointer',
      _hover: {
        bg: Content.Background.Hover,
        '> .collapse-tab-settings-icon': {
          color: Icon.Hover,
        },
      },
      _active: {
        bg: Content.Background.Active,
        '> .collapse-tab-settings-icon': {
          color: Icon.Active,
        },
      },
      _selected: {
        bg: SiteNavigationTabBackground.Selected,
        boxShadow: `0px 1px 0px 0px ${asChakraVariable(
          SiteNavigationTabBoxShadow.Selected,
        )}`,
      },
    },
    settingsIcon: {
      boxSize: '1.375rem',
      color: Icon.Default,
    },
    text: {
      textStyle: textStyleKeys.Body2,
      color: FontColor.Default,
      '.collapse-tab-content:hover &': {
        color: FontColor.Hover,
      },
      '.collapse-tab-content:active &': {
        color: FontColor.Active,
      },
    },
  },
  sizes: {
    [SiteNavigationCollapseTabSizes.Normal]: {
      container: {
        width: '10.75rem',
        height: '3rem',
        py: '0.5rem',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        borderRadius: '0.5rem',
        userSelect: 'none',
      },
      arrowIconWrapper: {
        boxSize: '1.5rem',
        direction: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      },
      arrowIcon: {
        boxSize: '0.95331rem',
        '.collapse-tab-content:hover &': {
          color: 'gray.1000',
        },
        '.collapse-tab-content:active &': {
          color: 'gray.1300',
        },
      },
    },
    [SiteNavigationCollapseTabSizes.Collapsed]: {
      container: {
        display: 'flex',
        padding: '0.75rem',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '0.75rem',
        alignSelf: 'stretch',
      },
      arrowIconWrapper: {
        boxSize: '1.75rem',
        direction: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        borderRadius: '2.5rem',
        _hover: {
          bg: Content.Background.Hover,
          '> .collapse-tab-arrow-icon': {
            color: Icon.Hover,
          },
        },
        _active: {
          bg: 'gray.500',
          '> .collapse-tab-arrow-icon': {
            color: Icon.Active,
          },
        },
      },
      arrowIcon: {
        boxSize: '1rem',
      },
    },
  },
  defaultProps: {
    size: SiteNavigationCollapseTabSizes.Normal,
  },
})

export const useSiteNavigationCollapseTabStyle = (
  props?: StyleThemingProps,
) => {
  return useCustomMultiStyleConfig<typeof SiteNavigationCollapseTabParts>(
    getName({ SiteNavigationCollapseTabStyle }),
    props,
  )
}
